import { useState } from "react";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Mail } from "lucide-react";

const schema = z.object({
  email: z.string().trim().email("Please enter a valid email").max(255),
});

interface NewsletterSignupProps {
  source?: string;
  variant?: "footer" | "block";
}

export const NewsletterSignup = ({ source = "footer", variant = "footer" }: NewsletterSignupProps) => {
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = schema.safeParse({ email });
    if (!parsed.success) {
      toast({
        title: "Invalid email",
        description: parsed.error.issues[0]?.message ?? "Please try again",
        variant: "destructive",
      });
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.from("newsletter_subscribers").insert({
      email: parsed.data.email.toLowerCase(),
      source,
    });
    setSubmitting(false);

    if (error) {
      // Unique-violation = already subscribed — treat as success
      if ((error as any).code === "23505") {
        setDone(true);
        toast({ title: "You're already on the list!", description: "Thanks for staying in touch." });
        return;
      }
      toast({
        title: "Couldn't subscribe",
        description: error.message,
        variant: "destructive",
      });
      return;
    }

    setDone(true);
    setEmail("");
    toast({ title: "You're subscribed!", description: "Thanks for joining the MSB Camps newsletter." });
  };

  if (done) {
    return (
      <p className={variant === "footer" ? "text-primary-foreground/80 text-sm" : "text-sm text-muted-foreground"}>
        Thanks — check your inbox soon for updates from MSB Camps.
      </p>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-2 w-full">
      <label htmlFor={`newsletter-email-${source}`} className="sr-only">
        Email address
      </label>
      <Input
        id={`newsletter-email-${source}`}
        type="email"
        placeholder="you@example.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
        disabled={submitting}
        className={
          variant === "footer"
            ? "bg-primary-foreground/10 border-primary-foreground/20 text-primary-foreground placeholder:text-primary-foreground/60"
            : ""
        }
      />
      <Button type="submit" disabled={submitting} variant={variant === "footer" ? "secondary" : "default"}>
        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
        {submitting ? "Subscribing..." : "Subscribe"}
      </Button>
    </form>
  );
};
